import { useEffect, useRef, useState } from "react";
import { BatteryLow, History, Loader2, Mail, PlugZap, QrCode, RefreshCw, Unplug, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { QrDialog } from "@/components/sms/QrDialog";
import { GmailDialog } from "@/components/sms/GmailDialog";
import { EmojiDialog } from "@/components/sms/EmojiDialog";
import { ScanDialog } from "@/components/sms/ScanDialog";
import { ago } from "@/components/sms/smsUtils";

const AMBER = ["bg-amber-500", "text-amber-800 bg-amber-50 border-amber-200"];
const SKY = ["bg-sky-500", "text-sky-800 bg-sky-50 border-sky-200"];
const TONE = {
  connected: ["bg-emerald-500", "text-emerald-800 bg-emerald-50 border-emerald-200", "Connected"],
  qr: [...SKY, "Waiting for QR scan"],
  gaia_starting: [...SKY, "Checking Gmail…"],
  emoji: [...SKY, "Tap the emoji on your phone"],
  connecting: [...AMBER, "Connecting"],
  reconnecting: [...AMBER, "Reconnecting"],
  phone_offline: [...AMBER, "Phone not responding"],
};
const RED = ["bg-red-500", "text-red-800 bg-red-50 border-red-200", "Not connected"];
const PAIRING = ["qr", "gaia_starting", "emoji"];
const LINKED = ["connected", "phone_offline", "reconnecting", "connecting"];
const QUIET = ["Phone not connected", "Phone disconnected", "Pairing cancelled"];
const btn = "h-9 px-3.5 inline-flex items-center gap-1.5 rounded-full text-xs font-semibold border transition-[background-color,transform] active:scale-95 disabled:opacity-50";
const GREEN = "bg-[#385C4B] text-white border-[#385C4B] hover:bg-[#2C4A3C]";
const LIGHT = "bg-white text-[#385C4B] border-[#385C4B]/40 hover:bg-[#EFEEE6]";

export const StatusBar = ({ status, onChanged }) => {
  const [busy, setBusy] = useState("");
  const [gmailOpen, setGmailOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const prev = useRef("");
  const now = status?.server_ts || Date.now();
  const alive = status?.helper_alive;
  const state = alive ? status?.state || "disconnected" : "service";
  const [dot, pill, label] = TONE[state] || RED;
  const failed = ["disconnected", "logged_out", "error"].includes(state) && status?.message && !QUIET.includes(status.message);
  const stale = status?.gmail_last_code_ts && now - status.gmail_last_code_ts > 30 * 60 * 1000;

  useEffect(() => {
    if (PAIRING.includes(prev.current) && failed) toast.error(status.message, { duration: 10000 });
    prev.current = state;
  }, [state, failed, status?.message]);

  const run = async (kind, body, ask = true) => {
    if (kind === "disconnect" && ask && !window.confirm("Phone ko app se disconnect karna hai?")) return;
    setBusy(kind);
    if (kind === "connect") setHidden(false);
    try {
      await api.post(`/sms/${kind}`, body || {});
      if (kind === "sync") toast.success("Sync started — last 100 chats check ho rahi hain");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const cancel = () => { setHidden(true); run("disconnect", null, false); };
  const B = ({ kind, icon: Icon, children, cls, onClick }) => (
    <button onClick={onClick || (() => run(kind, kind === "sync" ? { count: 100 } : null))} disabled={!!busy || !alive}
      data-testid={`sms-${kind}-btn`} className={`${btn} ${cls}`}>
      {busy === kind ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );

  return (
    <section className="bg-white border border-[#E0DFD6] rounded-xl p-4 sm:p-5 flex flex-wrap items-center gap-4" data-testid="sms-status-bar">
      <div className="flex-1 min-w-[240px] space-y-1.5">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#3A3A35]" data-testid="sms-gmail-title">
          <Mail className="w-4 h-4 text-[#385C4B]" /> Gmail link
          <span className="text-[10px] font-mono uppercase bg-[#EFEEE6] text-[#6B6B61] px-2 py-0.5 rounded-full">Backup</span>
        </div>
        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-semibold ${pill}`} data-testid="sms-status-state">
          <span className={`w-2 h-2 rounded-full ${dot} ${state === "connected" ? "animate-pulse" : ""}`} />
          {state === "service" ? "Phone link service starting…" : label}
          {state === "connected" && status?.account && <span className="font-normal">· {status.account}</span>}
        </span>
        <div className="text-xs text-[#6B6B61] flex flex-wrap gap-x-4 gap-y-1">
          {status?.message && state !== "connected" && !QUIET.includes(status.message) && (
            <span className={failed ? "text-red-700 font-semibold" : ""} data-testid="sms-status-message">{status.message}</span>
          )}
          <span className={stale ? "text-red-700 font-semibold" : ""} data-testid="sms-last-code">Last Gmail code: {ago(status?.gmail_last_code_ts, now)}</span>
          {status?.last_sync_ts && <span data-testid="sms-last-sync">Last sync: {ago(status.last_sync_ts, now)}{status.syncing ? " · syncing…" : ""}</span>}
          {status?.battery_low && <span className="text-amber-700 inline-flex items-center gap-1"><BatteryLow className="w-3.5 h-3.5" />Phone battery low</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {!LINKED.includes(state) && !PAIRING.includes(state) && (
          <>
            <B kind="gmail" icon={Mail} cls={GREEN} onClick={() => setGmailOpen(true)}>Connect with Gmail</B>
            <B kind="connect" icon={QrCode} cls={LIGHT}>QR</B>
          </>
        )}
        {PAIRING.includes(state) && <B kind="cancel" icon={X} cls={LIGHT} onClick={cancel}>Cancel</B>}
        {LINKED.includes(state) && <B kind="sync" icon={RefreshCw} cls={GREEN}>Sync Now</B>}
        {LINKED.includes(state) && <B kind="scan" icon={History} cls={LIGHT} onClick={() => setScanOpen(true)}>Full Scan</B>}
        {LINKED.includes(state) && state !== "connected" && <B kind="reconnect" icon={PlugZap} cls={LIGHT}>Reconnect</B>}
        {LINKED.includes(state) && <B kind="disconnect" icon={Unplug} cls="bg-white text-[#C8553D] border-[#C8553D]/40 hover:bg-red-50">Disconnect</B>}
      </div>
      <GmailDialog open={gmailOpen} onClose={() => setGmailOpen(false)} onSent={() => { setHidden(false); onChanged(); }} />
      <ScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onStarted={onChanged} />
      <QrDialog open={state === "qr" && !hidden} qrUrl={status?.qr_url} onClose={cancel} />
      <EmojiDialog open={state === "emoji" && !hidden} emoji={status?.emoji} emojiUrl={status?.emoji_url} account={status?.account} onClose={cancel} />
    </section>
  );
};
